import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReplayIcon from "@mui/icons-material/Replay";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InputAdornment from "@mui/material/InputAdornment";
import { getRequestWithToken } from "../../../utils/ApiRequests";
import ObjectIdBadge from "./ObjectIdBadge";

export const HIERARCHY_SEARCH_DELAY = 350;
export const canSearchHierarchy = (value) => value.trim().length >= 2;

const levelStyles = {
  sector: { color: "#5B5BD6", background: "rgba(91, 91, 214, 0.13)", Icon: CategoryOutlinedIcon },
  occupation: { color: "#E86F00", background: "rgba(232, 111, 0, 0.13)", Icon: AccountTreeOutlinedIcon },
  role: { color: "#25766C", background: "rgba(37, 118, 108, 0.13)", Icon: WorkOutlineIcon },
};

const ResultLevel = ({ level, name = "" }) => {
  const { color, background, Icon } = levelStyles[level];
  return (
    <Box display="flex" alignItems="center" gap={0.75} minWidth={0} sx={{ flex: { xs: "1 1 100%", sm: "0 1 auto" } }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ width: 26, height: 26, flexShrink: 0, borderRadius: 1.5, color, bgcolor: background }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography variant="body2" fontWeight={level === "role" ? 700 : 600} noWrap>
        {name}
      </Typography>
    </Box>
  );
};

const HierarchyQuickSearch = ({ onSelect = () => {}, onCopy = () => {} }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryToken, setRetryToken] = useState(0);
  const requestSequence = useRef(0);

  useEffect(() => {
    const query = inputValue.trim();
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    if (!canSearchHierarchy(query)) {
      setOptions([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getRequestWithToken(
          `../v3/admin/nco-hierarchy/search?query=${encodeURIComponent(query)}&limit=25`
        );
        if (requestId === requestSequence.current) {
          setOptions(response.data?.data?.results || []);
        }
      } catch (requestError) {
        if (requestId === requestSequence.current) {
          setOptions([]);
          setError(requestError?.response?.data?.msg || "Unable to search roles. Use the retry button or change the query.");
        }
      } finally {
        if (requestId === requestSequence.current) setLoading(false);
      }
    }, HIERARCHY_SEARCH_DELAY);

    return () => clearTimeout(timer);
  }, [inputValue, retryToken]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2.5,
        bgcolor: (theme) => theme.palette.mode === "dark" ? "#1b1b1b" : "#f1ede8",
        borderColor: (theme) => theme.palette.mode === "dark" ? "#303030" : "#d4cdc4",
        boxShadow: "0 8px 22px rgba(50, 38, 26, 0.08)",
      }}
    >
      <Box mb={1.25}>
        <Typography variant="subtitle1" fontWeight={750}>
          Quick role search
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Search a role and jump directly to its sector and occupation.
        </Typography>
      </Box>
      <Autocomplete
        options={options}
        value={null}
        inputValue={inputValue}
        loading={loading}
        filterOptions={(items) => items}
        getOptionLabel={(option) => option?.role?.name || ""}
        isOptionEqualToValue={(option, value) => (
          option?.role?._id === value?.role?._id && option?.sector?._id === value?.sector?._id
        )}
        onInputChange={(_, value, reason) => {
          if (reason !== "reset") setInputValue(value);
        }}
        onChange={(_, value) => {
          if (!value) return;
          setInputValue(value.role?.name || "");
          onSelect(value);
        }}
        noOptionsText={inputValue.trim().length < 2
          ? "Type at least 2 characters"
          : error || "No matching roles found"}
        PaperComponent={(paperProps) => (
          <Paper
            {...paperProps}
            elevation={8}
            sx={{
              mt: 0.75,
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: (theme) => theme.palette.mode === "dark" ? "#222" : "#f4f1ed",
              border: "1px solid",
              borderColor: (theme) => theme.palette.mode === "dark" ? "#383838" : "#d8d0c7",
            }}
          />
        )}
        ListboxProps={{ style: { maxHeight: 410, padding: 0 } }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Search by role name, NCO code, or role ObjectId..."
            error={Boolean(error)}
            helperText={error || "Select a result to reveal it in the existing cards."}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: (theme) => theme.palette.mode === "dark" ? "#242424" : "#faf8f5",
                borderRadius: 2,
                transition: "box-shadow 0.2s ease",
                "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(255, 140, 0, 0.12)" },
                "&.Mui-focused fieldset": { borderColor: "#FF8C00", borderWidth: 2 },
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {error && !loading ? (
                    <IconButton size="small" aria-label="Retry role search" onClick={() => setRetryToken((value) => value + 1)}>
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={`${option.sector?._id}-${option.occupation?._id}-${option.role?._id}`}
            sx={{
              alignItems: "flex-start !important",
              px: "16px !important",
              py: "12px !important",
              borderBottom: "1px solid",
              borderColor: (theme) => theme.palette.mode === "dark" ? "#333" : "#ded8d0",
              transition: "background-color 0.15s ease",
              "&:hover, &[aria-selected='true']": {
                bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,140,0,0.12) !important" : "#e9e2d9 !important",
              },
            }}
          >
            <Box minWidth={0} width="100%">
              <Box display="flex" alignItems="center" gap={0.6} minWidth={0} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
                <ResultLevel level="sector" name={option.sector?.name} />
                <ChevronRightIcon sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0, display: { xs: "none", sm: "block" } }} />
                <ResultLevel level="occupation" name={option.occupation?.name} />
                <ChevronRightIcon sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0, display: { xs: "none", sm: "block" } }} />
                <ResultLevel level="role" name={option.role?.name} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.8} gap={1}>
                <Box display="flex" gap={1.5} flexWrap="wrap" minWidth={0}>
                  <ObjectIdBadge objectId={option.sector?._id} onCopy={onCopy} compact label="Sector ID" />
                  <ObjectIdBadge objectId={option.occupation?._id} onCopy={onCopy} compact label="Occupation ID" />
                  <ObjectIdBadge objectId={option.role?._id} onCopy={onCopy} compact label="Role ID" />
                </Box>
                {option.role?.ncoCode && (
                  <Chip
                    label={`NCO ${option.role.ncoCode}`}
                    size="small"
                    sx={{ height: 22, flexShrink: 0, fontSize: 10, fontWeight: 700, bgcolor: "rgba(255,140,0,0.14)", color: "#C66100" }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      />
    </Paper>
  );
};

export default HierarchyQuickSearch;
